import {
  Box,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Grid2 as Grid,
  Divider,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Cake as CakeIcon,
  Badge as CpfIcon,
  Edit as EditIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { maskPhoneBR } from '../../utils/masks';
import { formatDate, getAge } from '../../utils/formatters';
import personsService from '../../services/personsService';
import { usePerson } from '../../hooks/queries/usePersonDetail';
import useDialog from '../../hooks/useDialog';
import LoadingState from '../ui/LoadingState';
import ErrorState from '../ui/ErrorState';
import PersonForm from '../forms/PersonForm';
import PersonNotesSection from './PersonNotesSection';
import PersonAppointmentsTimeline from './PersonAppointmentsTimeline';

/**
 * Conteúdo de detalhe de uma Person — usado pela rota full-screen e pelo modal.
 * Cabeçalho com dados + layout 2 colunas (ficha à esquerda, agendamentos à direita;
 * empilhado no mobile).
 */
export default function PersonDetailView({ personId, onClose }) {
  const queryClient = useQueryClient();
  const { person, loading, error, reload } = usePerson(personId);
  const editDialog = useDialog();

  const handleEditSubmit = async (data) => {
    await personsService.update(personId, data);
    queryClient.invalidateQueries({ queryKey: ['person', personId] });
    queryClient.invalidateQueries({ queryKey: ['persons'] });
    editDialog.handleClose();
  };

  if (loading) return <LoadingState message="Carregando pessoa..." />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!person) return null;

  return (
    <Box>
      {/* Cabeçalho */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Avatar src={person.photoUrl} alt={person.name} sx={{ width: 64, height: 64, bgcolor: 'grey.300', color: 'grey.600' }}>
            {!person.photoUrl && <PersonIcon sx={{ fontSize: 36 }} />}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h6" fontWeight="bold">{person.name}</Typography>
              {person.gender && <Chip label={person.gender} size="small" color="primary" />}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PhoneIcon sx={{ fontSize: 16 }} color="action" />
                <Typography variant="body2" color="text.secondary">{maskPhoneBR(person.phone || '')}</Typography>
              </Box>
              {person.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EmailIcon sx={{ fontSize: 16 }} color="action" />
                  <Typography variant="body2" color="text.secondary" noWrap>{person.email}</Typography>
                </Box>
              )}
              {person.cpf && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CpfIcon sx={{ fontSize: 16 }} color="action" />
                  <Typography variant="body2" color="text.secondary">{person.cpf}</Typography>
                </Box>
              )}
              {person.birthDate && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CakeIcon sx={{ fontSize: 16 }} color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(person.birthDate)}{getAge(person.birthDate) != null ? ` (${getAge(person.birthDate)} anos)` : ''}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Editar dados">
              <IconButton color="primary" onClick={() => editDialog.handleOpen(person)}><EditIcon /></IconButton>
            </Tooltip>
            {onClose && (
              <Tooltip title="Fechar">
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Corpo: ficha (esquerda) + agendamentos (direita); empilha no mobile */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <PersonNotesSection personId={personId} />
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <PersonAppointmentsTimeline personId={personId} />
          </Paper>
        </Grid>
      </Grid>

      {/* Edição de dados da pessoa */}
      <PersonForm
        open={editDialog.open}
        onClose={editDialog.handleClose}
        onSubmit={handleEditSubmit}
        data={editDialog.data}
      />
    </Box>
  );
}
